import { useCallback, useMemo } from "react";

import clsx from "clsx";
import { AnimatePresence } from "framer-motion";

import { LoadingSlide } from "@components/title-slide-view";
import {
  ModalPanel,
  PrimaryButton,
  WizardNavigation,
  WizardProgress,
  WizardProvider,
  WizardStepper,
} from "@components/v2";
import { GenericTooltip } from "@components/v2/tooltips/tooltip-container";
import {
  ADOPT_UNIVERSAL_BENCHMARKS,
  GET_ADOPTED_UNIVERSAL_BENCHMARKS_METADATA,
  GET_UNIVERSAL_BENCHMARKS_METADATA,
} from "@gql/universal-bentchmarks";
import { useFeatureFlags, useNorthbeamMutation } from "@hooks/http";
import {
  type AdoptUniversalBenchmarks,
  type AdoptUniversalBenchmarksVariables,
} from "@north-beam/nb-client-side";
import { todayLocalAsISODate } from "@north-beam/nb-common";
import { parseDateRangeArgument } from "@utils/index";

import { Chart } from "./chart";
import {
  WIZARD_STEP_ICONS,
  WIZARD_STEP_LABELS,
  WIZARD_STEPS,
} from "./constants";
import { useUniversalBenchmarksConfiguratorContext } from "./hooks";
import { ShoppingCart } from "./shopping-cart";
import { StartPage } from "./start-page";
import { AdjustForIncrementality } from "./wizard/adjust-for-incrementality";
import { AdjustForProfitability } from "./wizard/adjust-for-profitability";
import { AdjustForPromotionsAndOutliers } from "./wizard/adjust-for-promotions-and-outliers";
import { AdoptBenchmark } from "./wizard/adopt-benchmark";
import { SelectBaselineDays } from "./wizard/select-baseline-days";

interface Props {
  onComplete: () => void;
}

export const Configurator = ({ onComplete }: Props) => {
  const { hasFeatureFlag } = useFeatureFlags();
  const {
    isMetadataLoading,
    isBaselineDaysLoading,
    kpiMetricsData,
    baselineFilters,
    daysSelection,
    adoptableBenchmarksMetrics,
    adoptedMetrics,
    adoptableKpiMetrics,
    wizardStep,
    stage,
    setWizardStep,
    setStage,
    setBaselineFilters,
  } = useUniversalBenchmarksConfiguratorContext();

  const [adoptUniversalBenchmarks, { loading: isAdoptingBenchmark }] =
    useNorthbeamMutation<
      AdoptUniversalBenchmarks,
      AdoptUniversalBenchmarksVariables
    >(ADOPT_UNIVERSAL_BENCHMARKS);

  const adoptBenchmark = useCallback(async () => {
    const dateRangeInterval = parseDateRangeArgument(
      baselineFilters.dateRange,
      todayLocalAsISODate(),
    );

    const selectedDays = daysSelection
      .filter((e) => !e.isExcluded)
      .map((e) => e.day);

    const metricIdsToAdopt = adoptedMetrics.flatMap((e) => [
      e.metricId,
      `${e.metricId}Ft`,
      `${e.metricId}Rtn`,
    ]);

    const adoptedMetricIds = adoptableBenchmarksMetrics.metrics
      .filter((e) => metricIdsToAdopt.includes(e.id))
      .map((e) => e.id);

    await adoptUniversalBenchmarks({
      variables: {
        baselineFilters: {
          ...baselineFilters,
          dateRangeInterval,
        },
        selectedDays,
        metricIds: adoptedMetricIds,
      },
      refetchQueries: [
        {
          query: GET_ADOPTED_UNIVERSAL_BENCHMARKS_METADATA,
        },
        {
          query: GET_UNIVERSAL_BENCHMARKS_METADATA,
        },
      ],
      awaitRefetchQueries: true,
    });
  }, [
    baselineFilters,
    daysSelection,
    adoptableBenchmarksMetrics.metrics,
    adoptedMetrics,
  ]);

  const isKpiFiltersLoading = useMemo(() => {
    return kpiMetricsData.some((e) => e.status === "loading");
  }, [kpiMetricsData]);

  const metricToAdopt = useMemo(() => {
    return adoptableKpiMetrics.metrics.find(
      (e) => e.id === adoptedMetrics[0].metricId,
    );
  }, [adoptedMetrics, adoptableKpiMetrics]);

  const wizardOptions = useMemo(() => {
    return WIZARD_STEPS.map((step) => ({
      value: step,
      label: WIZARD_STEP_LABELS[step],
      icon: <i className={clsx("fa-regular", WIZARD_STEP_ICONS[step])}></i>,
    }));
  }, []);

  const selectedDaysCount = daysSelection.filter((e) => !e.isExcluded).length;

  if (isMetadataLoading) {
    return (
      <ModalPanel className="h-full">
        <LoadingSlide className="w-1/3" />
      </ModalPanel>
    );
  }

  return (
    <div
      className={clsx(
        "grid grid-rows-[315px_auto] gap-1.5 overflow-hidden h-full",
        "text-sm text-v2-primary relative",
      )}
    >
      <AnimatePresence>
        {stage === "intro" && (
          <StartPage
            onClose={() => {
              setBaselineFilters({
                ...baselineFilters,
                discountsThreshold: {
                  value: 20,
                  method: "blended",
                  enabled: true,
                },
                adjustment: null,
              });
              setStage("adopt");
            }}
            onComplete={onComplete}
          />
        )}
      </AnimatePresence>
      <ModalPanel className="bg-v2-primary px-4 pt-4 pb-2 overflow-hidden h-full">
        <Chart />
      </ModalPanel>
      <div className="grid grid-cols-[312px_auto] gap-1.5 overflow-hidden">
        <WizardProvider
          disabled={
            isBaselineDaysLoading || isAdoptingBenchmark || isKpiFiltersLoading
          }
          onChange={(step) => setWizardStep(step)}
          active={wizardStep}
          steps={wizardOptions}
        >
          <ModalPanel className="p-0 overflow-hidden">
            <div className="grid grid-rows-[auto_60px] h-full">
              <div className="py-6 px-5 h-full overflow-auto">
                <WizardStepper />
              </div>
              <WizardProgress className="rounded-b" />
            </div>
          </ModalPanel>
          <ModalPanel className="p-0 overflow-hidden">
            <div className="grid grid-rows-[auto_60px] h-full">
              {wizardStep === "adopt-benchmark" ? (
                <>
                  <div className="overflow-auto">
                    <AdoptBenchmark />
                  </div>
                  <div className="rounded-b px-6 py-3 border-t border-v2-tertiary">
                    {stage === "review" ? (
                      <PrimaryButton
                        className="w-full rounded-3xl font-bold tracking-wide"
                        onClick={() => {
                          setStage(
                            hasFeatureFlag("enableUniversalBenchmarksStartPage")
                              ? "intro"
                              : "adopt",
                          );
                          setWizardStep("select-baseline-days");
                        }}
                      >
                        UPDATE BENCHMARK
                      </PrimaryButton>
                    ) : (
                      <>
                        {metricToAdopt && (
                          <GenericTooltip
                            noInfoCircle
                            showTooltip={selectedDaysCount === 0}
                            content="You excluded all available days. This benchmark can't be adopted!"
                            placement="top"
                          >
                            <PrimaryButton
                              disabled={selectedDaysCount === 0}
                              className="w-full rounded-3xl font-bold tracking-wide"
                              loading={isAdoptingBenchmark}
                              onClick={async () => {
                                await adoptBenchmark();
                                onComplete();
                              }}
                            >
                              ADOPT {metricToAdopt.spec?.name} BENCHMARK
                            </PrimaryButton>
                          </GenericTooltip>
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-[auto_320px] overflow-hidden">
                    <div className="overflow-auto">
                      {wizardStep === "select-baseline-days" && (
                        <SelectBaselineDays />
                      )}
                      {wizardStep === "adjust-for-profitability" && (
                        <AdjustForProfitability />
                      )}
                      {wizardStep === "adjust-for-promotions-and-outliers" && (
                        <AdjustForPromotionsAndOutliers />
                      )}
                      {wizardStep === "adjust-for-incrementality" && (
                        <AdjustForIncrementality />
                      )}
                    </div>
                    <div className="py-6 pr-6 pl-3 overflow-hidden">
                      <ShoppingCart />
                    </div>
                  </div>
                  <WizardNavigation className="rounded-b" />
                </>
              )}
            </div>
          </ModalPanel>
        </WizardProvider>
      </div>
    </div>
  );
};
